import qs from 'querystring';

export default function HTTPService(authService) {
  const { REACT_APP_API_KEY } = process.env;
  const HTTP_METHOD_GET = 'GET';
  const HTTP_METHOD_POST = 'POST';
  const HTTP_METHOD_DELETE = 'DELETE';
  const HTTP_METHOD_PUT = 'PUT';
  const HTTP_METHOD_PATCH = 'PATCH';
  const HTTP_HEADER_ACCEPT = 'Accept';
  const HTTP_HEADER_CONTENT_TYPE = 'Content-Type';
  const HTTP_CREDENTIALS = 'credentials';
  const HTTP_CREDENTIALS_TYPE = 'include';
  const HTTP_API_KEY = 'x-api-key';
  const HTTP_AUTHORIZATION = 'Authorization';

  const MIME_TYPE_JSON = 'application/json;charset=utf-8';

  return {
    getAuthenticated,
    postAuthenticated,
    deleteAuthenticated,
    putAuthenticated,
    patchAuthenticated,
    downloadAuthenticated,
    post,
  };

  function getAuthenticated({ url, query }) {
    return makeRequest({
      method: HTTP_METHOD_GET,
      url,
      query,
      headers: {
        Authorization: `Bearer ${authService.getAccessToken()}`,
      },
    });
  }

  function downloadAuthenticated({ url, query }) {
    return makeBlobRequest({
      method: HTTP_METHOD_GET,
      url,
      query,
      headers: {
        Authorization: `Bearer ${authService.getAccessToken()}`,
        responseType: 'blob',
      },
    });
  }

  function postAuthenticated({ url, body }) {
    return makeRequest({
      method: HTTP_METHOD_POST,
      url,
      body,
      headers: {
        Authorization: `Bearer ${authService.getAccessToken()}`,
      },
    });
  }

  function deleteAuthenticated({ url, query, body }) {
    return makeRequest({
      method: HTTP_METHOD_DELETE,
      url,
      query,
      body,
      headers: {
        Authorization: `Bearer ${authService.getAccessToken()}`,
      },
    });
  }

  function putAuthenticated({ url, query, body }) {
    return makeRequest({
      method: HTTP_METHOD_PUT,
      url,
      query,
      body,
      headers: {
        Authorization: `Bearer ${authService.getAccessToken()}`,
      },
    });
  }

  function patchAuthenticated({ url, body }) {
    return makeRequest({
      method: HTTP_METHOD_PATCH,
      url,
      body,
      headers: {
        Authorization: `Bearer ${authService.getAccessToken()}`,
      },
    });
  }

  function post({ url, body }) {
    return makeRequest({
      method: HTTP_METHOD_POST,
      url,
      body,
      headers: {},
    });
  }

  async function makeRequest({
    method,
    url,
    query = {},
    body = {},
    headers = {},
  }) {
    const finalUrl =
      query && Object.keys(query).length
        ? `${url}?${qs.stringify(query)}`
        : url;

    const finalHeaders = Object.keys(headers).reduce((acc, headerKey) => {
      acc.append(headerKey, headers[headerKey]);
      return acc;
    }, new Headers());

    finalHeaders.set(HTTP_HEADER_ACCEPT, MIME_TYPE_JSON);

    if (
      method === HTTP_METHOD_POST ||
      method === HTTP_METHOD_PUT ||
      method === HTTP_METHOD_DELETE ||
      method === HTTP_METHOD_PATCH
    ) {
      finalHeaders.set(HTTP_HEADER_CONTENT_TYPE, MIME_TYPE_JSON);
    }

    finalHeaders.set(HTTP_API_KEY, REACT_APP_API_KEY);

    const requestDescription =
      method === HTTP_METHOD_POST ||
      method === HTTP_METHOD_PUT ||
      method === HTTP_METHOD_DELETE ||
      method === HTTP_METHOD_PATCH
        ? {
            method,
            headers: finalHeaders,
            body: JSON.stringify(body),
            [HTTP_CREDENTIALS]: HTTP_CREDENTIALS_TYPE,
          }
        : {
            method,
            headers: finalHeaders,
            [HTTP_CREDENTIALS]: HTTP_CREDENTIALS_TYPE,
          };

    let response;
    try {
      response = await fetch(finalUrl, requestDescription);
      
      if (response.status === 401 && authService) {
        const refreshToken = authService.getRefreshToken();
        
        if (refreshToken) {
          try {
            const refreshResponse = await fetch(`${url.split('/v1')[0]}/v1/auth/token/refresh`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'x-api-key': REACT_APP_API_KEY
              },
              body: JSON.stringify({ refresh: refreshToken }),
            });
            
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              authService.setAuthTokens(refreshData.access, refreshToken);
              
              finalHeaders.set(HTTP_AUTHORIZATION, `Bearer ${refreshData.access}`);
              const newResponse = await fetch(finalUrl, {
                ...requestDescription,
                headers: finalHeaders
              });
              
              response = newResponse;
            } else {
              authService.clearTokens();
              // Clear localStorage and redirect to login
              localStorage.clear();
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
            }
          } catch (refreshError) {
            console.error('Error refreshing token:', refreshError);
            authService.clearTokens();
            // Clear localStorage and redirect to login
            localStorage.clear();
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        } else {
          authService.clearTokens();
          // Clear localStorage and redirect to login
          localStorage.clear();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      }
    } catch (error) {
      response = error;
    }

    let responseBody = {};
    try {
      responseBody = await response.json();
    } catch (error) {}

    if (response.ok) {
      return responseBody;
    } else if (responseBody && Object.keys(responseBody).length > 0) {
      throw new Error(responseBody.message || responseBody.detail);
    } else {
      try {
        const responseText = await response.text();
        throw new Error(responseText);
      } catch (error) {
        throw new Error('Internal Server Error');
      }
    }
  }

  async function makeBlobRequest({
    method,
    url,
    query = {},
    body = {},
    headers = {},
  }) {
    const finalUrl =
      query && Object.keys(query).length
        ? `${url}?${qs.stringify(query)}`
        : url;

    const finalHeaders = Object.keys(headers).reduce((acc, headerKey) => {
      acc.append(headerKey, headers[headerKey]);
      return acc;
    }, new Headers());

    finalHeaders.set(HTTP_HEADER_ACCEPT, MIME_TYPE_JSON);

    if (
      method === HTTP_METHOD_POST ||
      method === HTTP_METHOD_PUT ||
      method === HTTP_METHOD_DELETE ||
      method === HTTP_METHOD_PATCH
    ) {
      finalHeaders.set(HTTP_HEADER_CONTENT_TYPE, MIME_TYPE_JSON);
    }

    finalHeaders.set(HTTP_API_KEY, REACT_APP_API_KEY);

    const requestDescription =
      method === HTTP_METHOD_POST ||
      method === HTTP_METHOD_PUT ||
      method === HTTP_METHOD_DELETE ||
      method === HTTP_METHOD_PATCH
        ? {
            method,
            headers: finalHeaders,
            body: JSON.stringify(body),
            [HTTP_CREDENTIALS]: HTTP_CREDENTIALS_TYPE,
          }
        : {
            method,
            headers: finalHeaders,
            [HTTP_CREDENTIALS]: HTTP_CREDENTIALS_TYPE,
          };

    let response;
    try {
      response = await fetch(finalUrl, requestDescription);
    } catch (error) {
      response = error;
    }

    if (response.ok) {
      return response.blob();
    } else {
      throw new Error('Internal Server Error');
    }
  }
}
